import React from 'react';
import { Box, Chip } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import { FilterOptions } from './FilterDialog';

interface ActiveFiltersChipsProps {
  filters: FilterOptions;
  onRemoveFilter: (filterKey: keyof FilterOptions) => void;
  onClearAll: () => void;
}

const ActiveFiltersChips: React.FC<ActiveFiltersChipsProps> = ({
  filters,
  onRemoveFilter,
  onClearAll,
}) => {
  const getActiveFilters = () => {
    const active: Array<{ key: keyof FilterOptions; label: string; value: string }> = [];

    if (filters.eventType !== 'all') {
      const eventType = filters.eventType.charAt(0).toUpperCase() + filters.eventType.slice(1);
      active.push({ key: 'eventType', label: 'Event', value: eventType });
    }

    if (filters.category !== 'all') {
      const categoryLabels: Record<string, string> = {
        tent: 'Decor',
        caters: 'Catering',
        photography: 'Photography',
        music: 'Music',
        makeup: 'Makeup',
        transport: 'Transport',
        hotel: 'Venues',
        catering: 'Catering',
        decoration: 'Decor',
      };
      const category =
        categoryLabels[filters.category] ||
        filters.category.charAt(0).toUpperCase() + filters.category.slice(1);
      active.push({ key: 'category', label: 'Category', value: category });
    }

    if (filters.location !== 'all') {
      const locationLabel =
        filters.location === 'nearby'
          ? 'Nearby (10 km)'
          : filters.location === 'custom'
          ? `Within ${filters.radius} km`
          : filters.location;
      active.push({ key: 'location', label: 'Location', value: locationLabel });
    }

    if (filters.budget !== 'all') {
      const budgetLabel =
        filters.minBudget > 0
          ? `₹${filters.minBudget.toLocaleString()} – ₹${filters.maxBudget.toLocaleString()}`
          : `Up to ₹${filters.maxBudget.toLocaleString()}`;
      active.push({ key: 'budget', label: 'Budget', value: budgetLabel });
    }

    if (filters.sortBy !== 'default') {
      const sortLabel =
        filters.sortBy === 'price-low'
          ? 'Price: Low to High'
          : filters.sortBy === 'price-high'
          ? 'Price: High to Low'
          : filters.sortBy === 'rating-high'
          ? 'Top rated'
          : filters.sortBy === 'rating-low'
          ? 'Rating: Low to High'
          : filters.sortBy === 'popular'
          ? 'Most popular'
          : filters.sortBy === 'newest'
          ? 'Newest'
          : filters.sortBy;
      active.push({ key: 'sortBy', label: 'Sort', value: sortLabel });
    }

    if (filters.minRating > 0) {
      active.push({
        key: 'minRating',
        label: 'Rating',
        value: `${filters.minRating}+ Stars`,
      });
    }

    return active;
  };

  const activeFilters = getActiveFilters();

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        alignItems: 'center',
        mb: 2,
      }}
    >
      <Chip
        label="Clear All"
        size="small"
        onClick={onClearAll}
        onDelete={onClearAll}
        deleteIcon={<ClearIcon />}
        color="default"
        variant="outlined"
      />
      {activeFilters.map((filter) => (
        <Chip
          key={filter.key}
          label={`${filter.label}: ${filter.value}`}
          size="small"
          onDelete={() => onRemoveFilter(filter.key)}
          color="primary"
          variant="outlined"
        />
      ))}
    </Box>
  );
};

export default ActiveFiltersChips;

